// src/pages/About/Components/AboutUs.jsx
import React, { useState } from 'react';
import {
  FaAward, FaShieldAlt, FaTruck, FaHeadphones,
  FaUsers, FaHeart, FaStar, FaLeaf, FaClock,
  FaInstagram, FaTwitter, FaYoutube,
  FaLinkedinIn, FaMapMarkerAlt, FaPhone, FaEnvelope,
  FaCalendarAlt, FaQuoteRight, FaLightbulb, FaPalette, FaTrophy,
  FaRegClock, FaCheckCircle, FaGem
} from 'react-icons/fa';

import face1 from "../../../img/face1.webp";
import face2 from "../../../img/face2.webp";
import face3 from "../../../img/face3.webp";
import face4 from "../../../img/face4.webp";

const accent = {
  base: '#8A9A7B',
  dark: '#6B7D5E',
  light: '#D4DCC9',
  bg: 'rgba(138,154,123,0.10)',
  bgHover: 'rgba(138,154,123,0.20)',
};

const stats = [
  { icon: FaUsers, value: '+۱۵۰۰', label: 'مشتری راضی' },
  { icon: FaStar, value: '۴.۹', label: 'میانگین امتیاز' },
  { icon: FaAward, value: '+۲۰۰', label: 'محصول منحصربه‌فرد' },
  { icon: FaClock, value: '۷ سال', label: 'تجربه درخشان' },
];

const features = [
  { icon: FaShieldAlt, title: 'ضمانت اصالت کالا', desc: 'همه محصولات با ضمانت اصالت ارائه می‌شوند.' },
  { icon: FaTruck, title: 'ارسال سریع', desc: 'تحویل سفارشات در کمترین زمان ممکن.' },
  { icon: FaHeadphones, title: 'پشتیبانی ۲۴/۷', desc: 'تیم پشتیبانی همیشه پاسخگوی شماست.' },
  { icon: FaLeaf, title: 'محصولات باکیفیت', desc: 'استفاده از بهترین متریال‌های روز دنیا.' },
  { icon: FaHeart, title: 'رضایت مشتری', desc: 'اولویت اول ما، رضایت شماست.' },
  { icon: FaAward, title: 'کیفیت برتر', desc: 'استانداردهای جهانی در تولید محصولات.' },
];

const contactInfo = [
  { 
    icon: FaMapMarkerAlt, 
    label: 'آدرس کارگاه', 
    value: 'مشهد، بلوار طرحچی، طرحچی ۳۲، خیابان صنعت، مجموعه تولیدی اریس گالری' 
  },
  { 
    icon: FaPhone, 
    label: 'شماره تماس', 
    value: '۰۹۹۰۳۶۰۷۵۲۵' 
  },
  { 
    icon: FaClock, 
    label: 'ساعت کاری', 
    value: '۸ صبح الی ۱۷' 
  },
];

const AboutUs = () => {
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [errors, setErrors] = useState({});
  const [sent, setSent] = useState(false);

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'نام الزامی است';
    if (!form.email.trim()) e.email = 'ایمیل الزامی است';
    else if (!/\S+@\S+\.\S+/.test(form.email)) e.email = 'ایمیل معتبر نیست';
    if (!form.message.trim()) e.message = 'پیام الزامی است';
    return e;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errors = validate();
    if (Object.keys(errors).length) { setErrors(errors); return; }
    setSent(true);
  };

  const field = (key) => ({
    value: form[key],
    onChange: (e) => {
      setForm(f => ({ ...f, [key]: e.target.value }));
      setErrors(er => { const n = { ...er }; delete n[key]; return n; });
    },
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50/80 font-sans" dir="rtl">
      <div className="relative overflow-hidden bg-gradient-to-br from-[#8A9A7B] via-[#9EAD8C] to-[#6B7D5E]">
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-72 h-72 bg-white/10 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-0 left-0 w-72 h-72 bg-white/10 rounded-full blur-3xl animate-pulse delay-700" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-white/10 rounded-full blur-3xl animate-pulse delay-1000" />
          <div className="absolute top-1/4 right-1/4 w-48 h-48 bg-white/5 rounded-full blur-2xl animate-pulse delay-500" />
        </div>

        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `radial-gradient(circle at 20% 50%, white 1px, transparent 1px), radial-gradient(circle at 80% 20%, white 1px, transparent 1px)`,
          backgroundSize: '60px 60px'
        }} />

        <div className="relative z-10 container mx-auto px-4 py-16 md:py-20">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="text-center md:text-right flex-1">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 mb-4 bg-white/20 backdrop-blur-sm rounded-full border border-white/30">
                <FaQuoteRight className="w-3.5 h-3.5 text-white/80" />
                <span className="text-white/90 text-xs md:text-sm font-medium">درباره ما</span>
              </div>
              
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight">
                درباره <span className="text-white/90">اریس گالری</span>
              </h1>
              
              <p className="text-white/80 text-sm md:text-base max-w-lg mx-auto md:mx-0 mt-3 leading-relaxed">
                جایی که هنر و کیفیت در کنار هم قرار می‌گیرند
              </p>
              
              <div className="flex flex-wrap justify-center md:justify-start gap-3 mt-6">
                <div className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20">
                  <FaRegClock className="w-3.5 h-3.5 text-white/70" />
                  <span className="text-white/80 text-xs font-medium">۷ سال تجربه</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20">
                  <FaCheckCircle className="w-3.5 h-3.5 text-white/70" />
                  <span className="text-white/80 text-xs font-medium">۱۵۰۰+ مشتری</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20">
                  <FaAward className="w-3.5 h-3.5 text-white/70" />
                  <span className="text-white/80 text-xs font-medium">۲۰۰+ محصول</span>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap justify-center gap-3 md:gap-4 flex-shrink-0">
              <div className="bg-white/10 backdrop-blur-md rounded-2xl px-4 py-3 text-center border border-white/20 min-w-[80px] hover:bg-white/20 transition-all duration-300">
                <div className="text-xl md:text-2xl font-bold text-white">+۱۵۰۰</div>
                <div className="text-[10px] text-white/60">مشتری</div>
              </div>
              <div className="bg-white/10 backdrop-blur-md rounded-2xl px-4 py-3 text-center border border-white/20 min-w-[80px] hover:bg-white/20 transition-all duration-300">
                <div className="text-xl md:text-2xl font-bold text-white">۴.۹</div>
                <div className="text-[10px] text-white/60">امتیاز</div>
              </div>
              <div className="bg-white/10 backdrop-blur-md rounded-2xl px-4 py-3 text-center border border-white/20 min-w-[80px] hover:bg-white/20 transition-all duration-300">
                <div className="text-xl md:text-2xl font-bold text-white">+۲۰۰</div>
                <div className="text-[10px] text-white/60">محصول</div>
              </div>
            </div>
          </div>
        </div>

        <div className="relative z-10">
          <svg className="w-full h-12 md:h-16" viewBox="0 0 1440 100" preserveAspectRatio="none">
            <path fill="#fafbf9" d="M0,40 C360,80 720,20 1080,60 L1440,40 L1440,100 L0,100 Z" />
          </svg>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12 space-y-16">
        <section className="bg-white rounded-3xl shadow-xl border border-gray-100/80 p-8 md:p-12 hover:shadow-2xl transition-all duration-300">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <span className="inline-block text-xs font-semibold px-4 py-1.5 rounded-full" style={{ background: accent.bg, color: accent.dark }}>
                <FaQuoteRight className="inline w-3 h-3 ml-1" />
                داستان ما
              </span>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mt-3 leading-snug">
                از یک ایده، تا ساختن یک <span style={{ color: accent.base }}>حس ماندگار</span>
              </h2>
            </div>

            <div className="relative">
              <div className="absolute right-0 top-0 bottom-0 w-0.5 hidden md:block" style={{ background: accent.bg }} />
              
              <div className="space-y-8 pr-0 md:pr-6">
                <div className="flex gap-4 items-start group">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 mt-1 group-hover:scale-110 transition-transform duration-300 shadow-sm" style={{ background: accent.bg }}>
                    <FaGem className="w-4 h-4" style={{ color: accent.base }} />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-800 text-base mb-2">زیبایی، از جزئیات شروع می‌شود</h3>
                    <p className="text-gray-500 text-[15px] leading-8 text-justify">
                      اریس با یک ایده ساده شکل گرفت؛ ساخت محصولاتی که فقط زیبا نباشند، بلکه 
                      <span className="text-gray-700 font-medium"> بخشی از لحظه‌های خوب خانه</span> باشند.
                      از یک پذیرایی ساده و صمیمی تا میزهایی که برای مهمان‌های خاص چیده می‌شوند، ما تلاش می‌کنیم با 
                      <span className="text-gray-700 font-medium"> طراحی متفاوت، کیفیت و توجه به جزئیات</span>، به هر لحظه شخصیت بدهیم.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4 items-start group">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 mt-1 group-hover:scale-110 transition-transform duration-300 shadow-sm" style={{ background: accent.bg }}>
                    <FaPalette className="w-4 h-4" style={{ color: accent.base }} />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-800 text-base mb-2">طراحی برای خانه‌های امروزی</h3>
                    <p className="text-gray-500 text-[15px] leading-8 text-justify">
                      در اریس، 
                      <span className="text-gray-700 font-medium"> چوب فقط یک متریال نیست؛ نقطه شروع طراحی است.</span>
                      ما اصالت و گرمای چوب را با 
                      <span className="text-gray-700 font-medium"> فرم‌های ساده و کاربردی</span> ترکیب می‌کنیم تا ظروف پذیرایی‌ای خلق کنیم که هم در استفاده روزمره دوست‌داشتنی باشند و هم در یک چیدمان خاص، چشمگیر.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4 items-start group">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 mt-1 group-hover:scale-110 transition-transform duration-300 shadow-sm" style={{ background: accent.bg }}>
                    <FaHeart className="w-4 h-4" style={{ color: accent.base }} />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-800 text-base mb-2">از یک کارگاه کوچک، تا یک نام دوست‌داشتنی</h3>
                    <p className="text-gray-500 text-[15px] leading-8 text-justify">
                      اریس از یک کارگاه کوچک شروع شد؛ با یک ایده، چند طرح و 
                      <span className="text-gray-700 font-medium"> اشتیاقی جدی برای متفاوت ساختن.</span>
                      امروز هر محصول اریس نتیجه تجربه، طراحی و دقتی است که طی سال‌ها شکل گرفته؛ با همان هدف روز اول:
                      ساخت محصولاتی که حضورشان در خانه، 
                      <span className="text-gray-700 font-medium"> حس خوبی ایجاد کند.</span>
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-10 p-6 rounded-2xl border border-gray-100/80 relative overflow-hidden" style={{ background: accent.bg }}>
                <div className="absolute top-0 right-0 text-6xl opacity-5" style={{ color: accent.base }}>
                  <FaQuoteRight className="w-12 h-12" />
                </div>
                <div className="absolute bottom-0 left-0 text-6xl opacity-5" style={{ color: accent.base }}>
                  <FaQuoteRight className="w-12 h-12 transform rotate-180" />
                </div>
                <p className="relative z-10 text-center text-sm md:text-base font-medium leading-relaxed" style={{ color: accent.dark }}>
                  "ما با عشق و تعهد، هنر را در دل چوب جاری می‌کنیم تا سینی‌هایی بی‌نظیر برای لحظات خاص شما خلق کنیم."
                </p>
                <p className="relative z-10 text-center text-xs mt-2 font-semibold" style={{ color: accent.dark }}>
                  — تیم اریس گالری
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {stats.map(({ icon: Icon, value, label }, i) => (
            <div key={i} className="bg-white rounded-2xl border border-gray-100/80 shadow-md p-6 text-center hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform duration-300" style={{ background: accent.bg }}>
                <Icon className="w-5 h-5" style={{ color: accent.base }} />
              </div>
              <div className="text-2xl font-bold text-gray-800">{value}</div>
              <div className="text-xs text-gray-400 mt-1">{label}</div>
            </div>
          ))}
        </section>

        <section>
          <div className="text-center mb-10">
            <span className="inline-block text-xs font-semibold px-4 py-1.5 rounded-full mb-3" style={{ background: accent.bg, color: accent.dark }}>
              ویژگی‌های ما
            </span>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-800">چرا <span style={{ color: accent.base }}>اریس گالری</span>؟</h2>
            <p className="text-gray-400 text-sm mt-2">شش دلیل برای انتخاب ما</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
            {features.map(({ icon: Icon, title, desc }, i) => (
              <div key={i} className="bg-white rounded-2xl border border-gray-100/80 shadow-md p-6 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300" style={{ background: accent.bg }}>
                  <Icon className="w-5 h-5" style={{ color: accent.base }} />
                </div>
                <h3 className="font-bold text-gray-800 mb-1 text-sm">{title}</h3>
                <p className="text-xs text-gray-400 leading-6">{desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section>
          <div className="text-center mb-10">
            <span className="inline-block text-xs font-semibold px-4 py-1.5 rounded-full mb-3" style={{ background: accent.bg, color: accent.dark }}>
              تیم ما
            </span>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-800">با <span style={{ color: accent.base }}>تیم</span> ما آشنا شوید</h2>
            <p className="text-gray-400 text-sm mt-2">افرادی که پشت صحنه اریس گالری هستند</p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            <div className="bg-white rounded-2xl border border-gray-100/80 shadow-md p-6 text-center hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group">
              <div className="w-20 h-20 rounded-full mx-auto mb-4 overflow-hidden shadow-lg group-hover:scale-105 transition-transform duration-300">
                <img src={face1} alt="محمدرضا ملازاده" className="w-full h-full object-cover" />
              </div>
              <h3 className="font-bold text-gray-800 text-sm">محمدرضا ملازاده</h3>
              <p className="text-xs text-gray-400 mb-3">مدیرعامل اریس گالری</p>
              <div className="flex justify-center gap-2">
                <a href="https://www.instagram.com/eriss_wood?stkn=Ymt6ODE0MzY1NWE2" className="p-2 rounded-full transition-all hover:scale-110 duration-200" style={{ background: accent.bg }}>
                  <FaInstagram className="w-3.5 h-3.5" style={{ color: accent.base }} />
                </a>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-gray-100/80 shadow-md p-6 text-center hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group">
              <div className="w-20 h-20 rounded-full mx-auto mb-4 overflow-hidden shadow-lg group-hover:scale-105 transition-transform duration-300">
                <img src={face2} alt="محمد گوهری" className="w-full h-full object-cover" />
              </div>
              <h3 className="font-bold text-gray-800 text-sm">محمد گوهری</h3>
              <p className="text-xs text-gray-400 mb-3">مدیر تولید و هنری</p>
              <div className="flex justify-center gap-2">
                <a href="#" className="p-2 rounded-full transition-all hover:scale-110 duration-200" style={{ background: accent.bg }}>
                  <FaInstagram className="w-3.5 h-3.5" style={{ color: accent.base }} />
                </a>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-gray-100/80 shadow-md p-6 text-center hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group">
              <div className="w-20 h-20 rounded-full mx-auto mb-4 overflow-hidden shadow-lg group-hover:scale-105 transition-transform duration-300">
                <img src={face3} alt="سمیرا ملازاده" className="w-full h-full object-cover" />
              </div>
              <h3 className="font-bold text-gray-800 text-sm">سمیرا ملازاده</h3>
              <p className="text-xs text-gray-400 mb-3">مدیر پشتیبانی و ارتباطات</p>
              <div className="flex justify-center gap-2">
                <a href="#" className="p-2 rounded-full transition-all hover:scale-110 duration-200" style={{ background: accent.bg }}>
                  <FaInstagram className="w-3.5 h-3.5" style={{ color: accent.base }} />
                </a>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-gray-100/80 shadow-md p-6 text-center hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group">
              <div className="w-20 h-20 rounded-full mx-auto mb-4 overflow-hidden shadow-lg group-hover:scale-105 transition-transform duration-300">
                <img src={face4} alt="تیم دیزیو" className="w-full h-full object-cover" />
              </div>
              <h3 className="font-bold text-gray-800 text-sm">تیم دیزیو</h3>
              <p className="text-xs text-gray-400 mb-3">توسعه‌دهندگان اریس گالری</p>
              <div className="flex justify-center gap-2">
                <a target="_blank" rel="noreferrer" href="https://www.instagram.com/dizio_design" className="p-2 rounded-full transition-all hover:scale-110 duration-200" style={{ background: accent.bg }}>
                  <FaInstagram className="w-3.5 h-3.5" style={{ color: accent.base }} />
                </a>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-white rounded-3xl border border-gray-100/80 shadow-xl p-6 md:p-8 lg:p-12 hover:shadow-2xl transition-all duration-300">
          <div className="flex flex-col sm:flex-row gap-6 sm:gap-8 lg:gap-10">
            
            <div className="sm:w-1/2">
              <span className="inline-block text-xs font-semibold px-4 py-1.5 rounded-full mb-3" style={{ background: accent.bg, color: accent.dark }}>
                ارتباط با ما
              </span>
              <h2 className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-800 mb-2">
                با ما در <span style={{ color: accent.base }}>ارتباط</span> باشید
              </h2>
              <p className="text-gray-400 text-sm mb-6 leading-relaxed">
                آماده شنیدن نظرات، پیشنهادات و سوالات شما هستیم.
              </p>
              
              <div className="space-y-3 md:space-y-4">
                {contactInfo.map(({ icon: Icon, label, value }, i) => (
                  <div 
                    key={i} 
                    className="flex items-center gap-3 md:gap-4 group p-2 md:p-3 rounded-xl hover:bg-gray-50/80 transition-all duration-300"
                  >
                    <div 
                      className="w-9 h-9 md:w-10 md:h-10 rounded-xl flex items-center justify-center shrink-0 transition-transform duration-300 group-hover:scale-110" 
                      style={{ background: accent.bg }}
                    >
                      <Icon className="w-3.5 h-3.5 md:w-4 md:h-4" style={{ color: accent.base }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] md:text-xs font-semibold text-gray-400 uppercase tracking-wider">{label}</p>
                      <p className="text-xs md:text-sm font-medium text-gray-700">{value}</p>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="flex flex-wrap gap-2 md:gap-3 pt-4 md:pt-6 border-t border-gray-100">
                <a href="https://www.instagram.com/eriss_wood" target="_blank" rel="noopener noreferrer" className="p-2 md:p-2.5 rounded-xl transition-all duration-300 hover:scale-110 hover:shadow-md" style={{ background: accent.bg }}>
                  <FaInstagram className="w-3.5 h-3.5 md:w-4 md:h-4" style={{ color: accent.base }} />
                </a>
                <a href="#" className="p-2 md:p-2.5 rounded-xl transition-all duration-300 hover:scale-110 hover:shadow-md" style={{ background: accent.bg }}>
                  <FaTwitter className="w-3.5 h-3.5 md:w-4 md:h-4" style={{ color: accent.base }} />
                </a>
                <a href="#" className="p-2 md:p-2.5 rounded-xl transition-all duration-300 hover:scale-110 hover:shadow-md" style={{ background: accent.bg }}>
                  <FaYoutube className="w-3.5 h-3.5 md:w-4 md:h-4" style={{ color: accent.base }} />
                </a>
                <a href="#" className="p-2 md:p-2.5 rounded-xl transition-all duration-300 hover:scale-110 hover:shadow-md" style={{ background: accent.bg }}>
                  <FaLinkedinIn className="w-3.5 h-3.5 md:w-4 md:h-4" style={{ color: accent.base }} />
                </a>
              </div>
            </div>

            <div className="sm:w-1/2 mt-4 sm:mt-0">
              {sent ? (
                <div className="flex flex-col items-center justify-center text-center gap-3 md:gap-4 py-10 md:py-12 px-4">
                  <div className="w-14 h-14 md:w-16 md:h-16 rounded-full flex items-center justify-center text-2xl md:text-3xl shadow-lg" style={{ background: accent.bg }}>
                    <FaCheckCircle className="w-7 h-7 md:w-8 md:h-8" style={{ color: accent.base }} />
                  </div>
                  <div>
                    <p className="font-bold text-gray-800 text-base md:text-lg">پیام شما ارسال شد!</p>
                    <p className="text-xs text-gray-400 mt-1">به زودی با شما تماس می‌گیریم.</p>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} noValidate className="space-y-3 md:space-y-4">
                  {[
                    { key: 'name', label: 'نام کامل', type: 'text', placeholder: 'نام و نام خانوادگی' },
                    { key: 'email', label: 'ایمیل', type: 'email', placeholder: 'ایمیل شما' },
                  ].map(({ key, label, type, placeholder }) => (
                    <div key={key}>
                      <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                        {label}
                        <span className="text-red-400 mr-0.5">*</span>
                      </label>
                      <input
                        type={type}
                        placeholder={placeholder}
                        {...field(key)}
                        className={`w-full px-4 py-2.5 md:py-3 text-sm rounded-xl border-2 outline-none transition-all duration-300 ${
                          errors[key] 
                            ? 'border-red-400 focus:border-red-400' 
                            : 'border-gray-200 focus:border-[#8A9A7B] focus:shadow-md'
                        }`}
                      />
                      {errors[key] && (
                        <p className="text-[10px] text-red-500 mt-1 flex items-center gap-1">
                          <span className="inline-block w-1 h-1 bg-red-500 rounded-full" />
                          {errors[key]}
                        </p>
                      )}
                    </div>
                  ))}
                  
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                      پیام
                      <span className="text-red-400 mr-0.5">*</span>
                    </label>
                    <textarea
                      rows={4}
                      placeholder="پیام شما"
                      {...field('message')}
                      className={`w-full px-4 py-2.5 md:py-3 text-sm rounded-xl border-2 outline-none transition-all duration-300 resize-none ${
                        errors.message 
                          ? 'border-red-400 focus:border-red-400' 
                          : 'border-gray-200 focus:border-[#8A9A7B] focus:shadow-md'
                      }`}
                    />
                    {errors.message && (
                      <p className="text-[10px] text-red-500 mt-1 flex items-center gap-1">
                        <span className="inline-block w-1 h-1 bg-red-500 rounded-full" />
                        {errors.message}
                      </p>
                    )}
                  </div>
                  
                  <button
                    type="submit"
                    className="w-full py-3 md:py-3.5 rounded-xl text-sm font-semibold text-white transition-all duration-300 shadow-lg hover:shadow-xl active:scale-[.98] hover:opacity-90"
                    style={{ background: `linear-gradient(135deg, ${accent.base}, ${accent.dark})` }}
                  >
                    ارسال پیام
                  </button>
                </form>
              )}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default AboutUs;